/* GridCellRenderer.js */

"use strict";

const _ = require('lodash');

class GridCellRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-cell");
        this._rootElem.component = this._model;

        let colRendererComponent = this._model.getColumnRendererComponent();
        this._cellRendererComponent = new colRendererComponent({
            value: this._model.getValue()
        });

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        this._unregisterListeners();
        return this._cellRendererComponent.shutdown()
            .then(() => {
                this._cellRendererComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            });
    }
    _registerListeners(){
        this._model.on("ID",this._onId = this._onId.bind(this));
        this._model.on("Value",this._onValue = this._onValue.bind(this));
        this._model.on("CentralColumn",this._onCentralColumn = this._onCentralColumn.bind(this));
        this._model.on("Width",this._onWidth = this._onWidth.bind(this));
        this._cellRendererComponent.on('UserCellAction', this._onUserCellAction = this._onUserCellAction.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("ID",this._onId);
        this._model.removeListener("Value",this._onValue);
        this._model.removeListener("CentralColumn",this._onCentralColumn);
        this._model.removeListener("Width",this._onWidth);
        this._cellRendererComponent.removeListener('UserCellAction', this._onUserCellAction);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._rootElem.innerHTML = '';
            this._rootElem.appendChild(this._cellRendererComponent.renderer.rootElement);
            let centralColumn = this._model.getCentralColumn();
            this._setId(this._model.getId());
            this._setCentralColumn(centralColumn);
            this._setWidth(this._model.getWidth());
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the id has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onId(newVal){
        this._setId(newVal);
    }
    
    /**
     * Fired when the value has been updated in the model
     * @param newVal {boolean|object|number|array|string}
     * @private
     */
    _onValue(newVal){
        this._setValue(newVal);
    }

    /**
     * Fired when the central column state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onCentralColumn(newVal){
        this._setCentralColumn(newVal);
    }

    /**
     * Fired when the width has been updated in the model
     * @param newVal ()
     * @private
     */
    _onWidth(newVal){
        this._setWidth(newVal);
    }

    /**
     * Firec when the user has selected an action inside of the cell
     * @param eventData
     * @private
     */
    _onUserCellAction(eventData){
        this._model._triggerCellAction(eventData);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the id as a class in the DOM
     * @param newVal {string}
     * @private
     */
    _setId(newVal){
        if (!_.isUndefined(newVal)) {
            this._rootElem.classList.add(newVal);
        }
    }
    
    /**
     * Toggle the central column state
     * @param newVal {boolean}
     * @private
     */
    _setCentralColumn(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('central');
        } else {
            this._rootElem.classList.remove('central');
        }
    }

    /**
     * Seth the width
     * @param newVal {number}
     * @private
     */
    _setWidth(newVal){
        if (this._model.getCentralColumn() !== true) {
            this._rootElem.style.width = (newVal * 100) + "%";
        }
    }

    /**
     * Pass the new value along to the cell renderer component
     * @param newVal {boolean}
     * @private
     */
    _setValue(newVal){
        this._cellRendererComponent.setValue(newVal);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridCellRenderer;